import { snapAngle, snapPoint } from './canvasConstants';

export interface Pt {
  x: number;
  y: number;
}

/** A dashed alignment guide in canvas coordinates. */
export interface GuideLine {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

const ALIGN_SCREEN_PX = 8;
const VERTEX_SCREEN_PX = 12;

function nearest(p: Pt, pts: Pt[], threshold: number): Pt | null {
  let best: Pt | null = null;
  let bestD = threshold;
  for (const q of pts) {
    const d = Math.hypot(q.x - p.x, q.y - p.y);
    if (d <= bestD) {
      bestD = d;
      best = q;
    }
  }
  return best;
}

function verticalGuide(x: number, yA: number, yB: number): GuideLine {
  const pad = 72;
  return { x1: x, y1: Math.min(yA, yB) - pad, x2: x, y2: Math.max(yA, yB) + pad };
}

function horizontalGuide(y: number, xA: number, xB: number): GuideLine {
  const pad = 72;
  return { x1: Math.min(xA, xB) - pad, y1: y, x2: Math.max(xA, xB) + pad, y2: y };
}

/**
 * Snap a drawing cursor to existing corners and to horizontal/vertical
 * alignments. Locked axes stay on the aligned coordinate; the free axis
 * still snaps to the grid when snapping is on.
 */
export function resolveDrawingPoint(args: {
  raw: Pt;
  prev: Pt | null;
  anchors: Pt[];
  scale: number;
  ortho: boolean;
  snapGrid: boolean;
}): { point: Pt; guides: GuideLine[] } {
  const { raw, prev, anchors, scale, ortho, snapGrid } = args;
  const safeScale = Math.max(scale, 0.05);

  const vertex = nearest(raw, anchors, VERTEX_SCREEN_PX / safeScale);
  if (vertex) return { point: vertex, guides: [] };

  if (ortho && prev) {
    return { point: snapPoint(snapAngle(prev, raw), snapGrid), guides: [] };
  }

  const threshold = ALIGN_SCREEN_PX / safeScale;
  let x = raw.x;
  let y = raw.y;
  let lockX = false;
  let lockY = false;
  const guides: GuideLine[] = [];

  let bestX: { v: number; d: number; ay: number } | null = null;
  let bestY: { v: number; d: number; ax: number } | null = null;
  for (const a of anchors) {
    const dx = Math.abs(a.x - raw.x);
    const dy = Math.abs(a.y - raw.y);
    if (dx <= threshold && (!bestX || dx < bestX.d)) bestX = { v: a.x, d: dx, ay: a.y };
    if (dy <= threshold && (!bestY || dy < bestY.d)) bestY = { v: a.y, d: dy, ax: a.x };
  }
  if (bestX) {
    x = bestX.v;
    lockX = true;
    guides.push(verticalGuide(bestX.v, raw.y, bestX.ay));
  }
  if (bestY) {
    y = bestY.v;
    lockY = true;
    guides.push(horizontalGuide(bestY.v, raw.x, bestY.ax));
  }

  // Continue the previous wall point on a straight horizontal or vertical line,
  // but don't pull the cursor back onto that point itself.
  if (prev) {
    const dx = Math.abs((lockX ? x : raw.x) - prev.x);
    const dy = Math.abs((lockY ? y : raw.y) - prev.y);
    if (!lockX && dx <= threshold && dy > threshold) {
      x = prev.x;
      lockX = true;
      guides.push(verticalGuide(prev.x, raw.y, prev.y));
    }
    if (!lockY && dy <= threshold && dx > threshold) {
      y = prev.y;
      lockY = true;
      guides.push(horizontalGuide(prev.y, raw.x, prev.x));
    }
  }

  const grid = snapPoint({ x, y }, snapGrid);
  return {
    point: { x: lockX ? x : grid.x, y: lockY ? y : grid.y },
    guides,
  };
}

/** Place a point `pixels` away from `from`, in the direction of `toward`. */
export function pointAtDistance(from: Pt, toward: Pt, pixels: number): Pt {
  const dx = toward.x - from.x;
  const dy = toward.y - from.y;
  const dist = Math.hypot(dx, dy);
  if (dist < 0.001) return { x: from.x + pixels, y: from.y };
  return { x: from.x + (dx / dist) * pixels, y: from.y + (dy / dist) * pixels };
}
